/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { openDB } from "idb";
import type { IDBPDatabase } from "idb";
import type { KvStore } from "@tivocorp/sage-web-sdk/api";

const DB_NAME = "sage_kv";
const STORE_NAME = "kv";
const DB_VERSION = 1;

/**
 * KvStore implementation backed by IndexedDB.
 * Uses the "sage_kv" database with a "kv" object store.
 * Separate from the "sage" database used by ChunkStore (openSageDatabase).
 */
export class SageKvStore implements KvStore {
  private db: IDBPDatabase | null = null;

  private async getDb(): Promise<IDBPDatabase> {
    if (this.db === null) {
      this.db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db: IDBPDatabase): void {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME);
          }
        }
      });
    }
    return this.db;
  }

  public async getString(key: string): Promise<string | null> {
    const db = await this.getDb();
    const value: string | undefined = await db.get(STORE_NAME, key);
    return value === undefined ? null : value;
  }

  public async putString(key: string, value: string): Promise<void> {
    const db = await this.getDb();
    await db.put(STORE_NAME, value, key);
  }

  public async remove(key: string): Promise<void> {
    const db = await this.getDb();
    await db.delete(STORE_NAME, key);
  }
}
